"use client";

import { useEffect, useState } from "react";
import { Phone, MessageSquare, Menu as MenuIcon, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuItems({
  items
}: {
  items: MenuItem[];
}) {
  return <NavigationMenuList className="gap-0.5 lg:gap-1">
      {items.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
          {item.children && item.children.length > 0 ? <>
              <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-muted focus:bg-muted font-medium text-xs lg:text-sm px-2.5 lg:px-4 py-1.5 lg:py-2">
                {item.label}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-56 gap-1 p-2 bg-popover text-popover-foreground rounded-lg border border-border shadow-md">
                  {item.children.map((child, childIndex) => <li key={child.id} data-index={childIndex}>
                      {child.children && child.children.length > 0 ? <div className="px-3 py-2">
                          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1">
                            {child.label}
                          </span>
                          <ul className="pl-2 border-l border-border space-y-1">
                            {child.children.map((grandChild, grandIndex) => <li key={grandChild.id} data-index={grandIndex}>
                                <NavigationMenuLink asChild>
                                  <Link to={grandChild.href || "#"} className="block rounded-md px-2 py-1.5 text-xs text-foreground hover:bg-muted hover:text-foreground transition-colors">
                                    {grandChild.label}
                                  </Link>
                                </NavigationMenuLink>
                              </li>)}
                          </ul>
                        </div> : <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none rounded-md px-3 py-2 text-sm leading-none text-foreground no-underline outline-none transition-colors hover:bg-muted hover:text-foreground">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>}
                    </li>)}
                </ul>
              </NavigationMenuContent>
            </> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} {.../^https?:\/\//.test(item.href || "") && !(item.href || "").includes("gn-construction.com") ? {
          target: "_blank",
          rel: "noopener noreferrer"
        } : {}} className={`${navigationMenuTriggerStyle()} bg-transparent text-foreground hover:bg-muted focus:bg-muted font-medium text-xs lg:text-sm px-2.5 lg:px-4 py-1.5 lg:py-2 transition-colors`}>
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </NavigationMenuItem>)}
    </NavigationMenuList>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-1.5 lg:gap-2">
        <div className="h-8 w-14 lg:w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-16 lg:w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-14 lg:w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 lg:w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <DesktopMenuItems items={menuItems} />
    </NavigationMenu>;
}
function MobileMenuItemsList({
  items,
  onItemClick
}: {
  items: MenuItem[];
  onItemClick: () => void;
}) {
  return <ul className="space-y-1">
      {items.map((item, i) => <li key={item.id} data-index={i} className="flex flex-col">
          <Link to={item.href || "#"} {.../^https?:\/\//.test(item.href || "") && !(item.href || "").includes("gn-construction.com") ? {
        target: "_blank",
        rel: "noopener noreferrer"
      } : {}} onClick={onItemClick} className="flex items-center justify-between py-2.5 px-3 rounded-md text-foreground font-medium text-base hover:bg-muted transition-colors">
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 && <div className="pl-4 ml-2 border-l border-border mt-1 mb-2">
              <MobileMenuItemsList items={item.children} onItemClick={onItemClick} />
            </div>}
        </li>)}
    </ul>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3 py-4">
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <MobileMenuItemsList items={menuItems} onItemClick={onNavigate} />;
}
export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /* ===== GNCI Meta pixel (added Sept 18, 2026) =============================
     Dataset: GNCI 2181683919055042.
     Mirrors the existing GA4 event layer into Meta by wrapping window.gtag,
     so there is one source of truth for events and the two cannot drift.
     Rollback: delete this one useEffect block.
     ======================================================================== */
  useEffect(() => {
    const PIXEL_ID = "2181683919055042";
    if (typeof window === "undefined") return;
    const w = window as any;
    if (!w.fbq) {
      /* eslint-disable */
      (function (f: any, b: any, e: string, v: string, n?: any, t?: any, s?: any) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
      /* eslint-enable */
      w.fbq("init", PIXEL_ID);
    }
    const fbq = w.fbq;
    fbq("track", "PageView");
    const service = window.location.pathname.match(/\/(adu-construction|home-additions|kitchen-remodeling|bathroom-remodeling|roofing|painting)\//);
    if (service) {
      fbq("track", "ViewContent", {
        content_name: service[1],
        content_category: "service_page"
      });
    }
    const GA4_TO_META: Record<string, string> = {
      booking_start: "InitiateCheckout",
      generate_lead: "Lead",
      phone_click: "Contact",
      text_click: "Contact"
    };
    const originalGtag = w.gtag;
    if (typeof originalGtag === "function" && !originalGtag.__gnciMetaWrapped) {
      const wrapped = function (this: any, ...args: any[]) {
        try {
          if (args[0] === "event") {
            const metaEvent = GA4_TO_META[args[1]];
            if (metaEvent) {
              fbq("track", metaEvent, {
                content_name: (args[2] || {}).service || undefined,
                content_category: args[1]
              });
            }
          }
        } catch (e) {
          /* tracking must never be allowed to break the page */
        }
        return originalGtag.apply(this, args);
      };
      (wrapped as any).__gnciMetaWrapped = true;
      w.gtag = wrapped;
    }
  }, []);

  // Google Ads website call tracking (account 760-298-4233).
  // Visitors who came from an ad see a Google forwarding number that rings (909) 906-3355.
  // Text (sms:) links keep the real number because forwarding numbers can't receive texts.
  /* GNCI Meta click tagging, added Sept 19 2026.
     The booking form already stores the Google click ids (gclid, gbraid,
     wbraid) and the five utm values in sessionStorage under gnc_<name>, and
     sends them on with every booking. Facebook and Instagram clicks arrive
     carrying fbclid, which was not in that list, so a Meta visitor who booked
     was filed as direct or organic and nothing could be reported back to Meta.
     This writes the Meta click into the keys the form already reads, so the
     seven copies of the booking component do not need to change.
     Real utm values on the link always win: we only fill in when utm_source is
     empty. Medium is recorded as social rather than paid_social because fbclid
     appears on organic Facebook links too, so it alone does not prove paid.
     Paid Meta links should carry their own utm parameters.
     Rollback: delete this one useEffect block.
     Doc: claude/change-log-2026-09-19-source-tagging.md */
  useEffect(() => {
    const apply = () => {
      try {
        const q = new URLSearchParams(window.location.search);
        const fb = q.get('fbclid');
        if (!fb) return;
        const ss = window.sessionStorage;
        if (!ss) return;
        if (!ss.getItem('gnc_utm_source')) {
          ss.setItem('gnc_utm_source', 'facebook');
          ss.setItem('gnc_utm_medium', 'social');
        }
        if (!ss.getItem('gnc_utm_content')) ss.setItem('gnc_utm_content', 'fbclid_' + fb);
        ss.setItem('gnc_fbclid', fb);
      } catch (err) {}
    };
    apply();
    const t1 = window.setTimeout(apply, 1200);
    const t2 = window.setTimeout(apply, 3000);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  /* GNCI financing cue inside the booking form, added Sept 18 2026.
     The financing section sits at the bottom of every page, below the booking
     form, so a homeowner who hesitates over budget while choosing an
     appointment never reaches it. This puts one quiet line at that decision
     point. The wording is taken from the already published financing section
     so no new financing claim is introduced. The link opens in a new tab on
     purpose so the customer keeps their place in the booking form.
     Rollback: delete this one useEffect block.
     Doc: claude/change-log-2026-09-18-financing-and-galleries.md */
  useEffect(() => {
    const RENOFI = 'https://pros.renofi.com/pros/good-neighbor-construction';
    const place = () => {
      try {
        if (document.getElementById('gnci-fin-cue')) return true;
        const sched = document.getElementById('schedule');
        if (!sched) return false;
        const form = sched.querySelector('form');
        if (!form) return false;
        const step = form.querySelector('fieldset');
        if (!step) return false;
        const p = document.createElement('p');
        p.id = 'gnci-fin-cue';
        p.className = 'mt-4 text-sm text-muted-foreground';
        p.appendChild(document.createTextNode('Financing is available through our partner RenoFi. Checking your options will not impact your credit. '));
        const a = document.createElement('a');
        a.href = RENOFI;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.className = 'text-primary underline underline-offset-2';
        a.textContent = 'See financing options';
        p.appendChild(a);
        step.appendChild(p);
        return true;
      } catch (err) {
        return false;
      }
    };
    let obs: any = null;
    const attach = () => {
      const sched = document.getElementById('schedule');
      if (!sched) return false;
      place();
      if (obs) obs.disconnect();
      obs = new MutationObserver(() => {
        place();
      });
      obs.observe(sched, {
        childList: true,
        subtree: true
      });
      return true;
    };
    if (!attach()) {
      obs = new MutationObserver(() => {
        attach();
      });
      obs.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    return () => {
      if (obs) obs.disconnect();
    };
  }, []);

  /* ===== GNCI conversion routing (added Sept 18, 2026) =====================
     Any Start Your Project style link that points at the homepage intake
     (#request-estimate) is sent to this page's own booking section (#schedule)
     when the page has one. The visitor stays on the service page, so the
     service they were reading is preserved and they never re-pick it.
     Pages with no #schedule (homepage, About, Projects) are left unchanged.
     Capture phase is required: the theme has a global click handler that
     otherwise swallows anchor scrolling.
     Rollback: delete this one useEffect block.
     Doc: claude/change-log-2026-09-18-cta-routing.md  */
  useEffect(() => {
    const onDocClick = (e: any) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const a = t && t.closest ? t.closest('a') as HTMLAnchorElement | null : null;
      if (!a) return;
      const raw = a.getAttribute('href') || '';
      const label = (a.textContent || '').replace(/\s+/g, ' ').trim();
      /* Catches three broken variants seen in the Sept 18 audit:
         #request-estimate, /test-page/#request-estimate, and a bare / link. */
      const isCta = raw.indexOf('request-estimate') !== -1 || /^start\s+(your\s+)?project$/i.test(label);
      if (!isCta) return;
      const target = document.getElementById('schedule');
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      const inOverlay = !!a.closest('[role="dialog"]');
      const go = () => {
        const el = document.getElementById('schedule');
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({
          top: top < 0 ? 0 : top,
          behavior: 'smooth'
        });
        try {
          window.history.replaceState(null, '', '#schedule');
        } catch (err) {}
      };
      if (inOverlay) {
        /* Close the mobile sheet through its own Close control so the overlay
           library runs its normal cleanup, with Escape as a fallback. */
        const dlg = a.closest('[role="dialog"]') as HTMLElement | null;
        const x = dlg ? dlg.querySelector('svg.lucide-x') : null;
        const closeBtn = x ? x.closest('button') as HTMLElement | null : null;
        if (closeBtn) {
          closeBtn.click();
        } else {
          document.dispatchEvent(new KeyboardEvent('keydown', {
            key: 'Escape',
            bubbles: true
          }));
        }
        window.setTimeout(go, 320);
        /* Safety net: if the overlay ever fails to release its scroll lock the
           page would be left unclickable and unscrollable. Once no sheet is
           open, release it. */
        window.setTimeout(() => {
          if (document.querySelector('[role="dialog"][data-state="open"]')) return;
          const b = document.body;
          if (!b) return;
          if (window.getComputedStyle(b).pointerEvents === 'none') b.style.pointerEvents = 'auto';
          if (window.getComputedStyle(b).overflow.indexOf('hidden') !== -1) b.style.overflow = '';
        }, 900);
      } else {
        go();
      }
    };
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, []);

  /* ===== GNCI engagement tracking (added Sept 18, 2026) ====================
     Sends engagement events to the existing Google tag. No second tag and no
     tag-manager container: this rides the GT- tag Site Kit already loads.
     These are ENGAGEMENT events only. The lead itself stays the single
     conversion, fired once by the booking widget, so nothing is double
     counted. Do not mark any of these as a key event in GA4.
     Every event carries the service of the page, so funnels can be split by
     service without extra setup.
     Rollback: delete this one useEffect block.
     Doc: claude/change-log-2026-09-18-ga4-events.md  */

  /* ===== Microsoft Clarity (added Sept 18, 2026) =========================
     Heat maps and session recordings. Project yk9mdn2iu2.
     Loaded from the one component that renders on every page, so there is a
     single place to install, audit and remove it. No plugin, no second tag
     manager.
     Privacy: masking is set in the Clarity dashboard so that everything a
     visitor types into a form field (name, phone, email, address) is masked
     before it leaves the browser. The booking form is additionally marked
     with data-clarity-mask below as a belt-and-braces measure.
     Rollback: delete this block and publish, then pause the project in
     Clarity. Doc: claude/change-log-2026-09-18-clarity.md  */
  useEffect(() => {
    const w = window as any;
    if (w.__gnciClarity) return;
    /* Do not record our own sessions. The WordPress admin bar only renders for
       a logged-in admin, which is the owner or anyone doing site work, so their
       browsing never lands in the heat maps or recordings. Visitors never see
       that element, so they are recorded normally. */
    if (document.getElementById("wpadminbar")) return;
    w.__gnciClarity = true;
    try {
      w.clarity = w.clarity || function () {
        (w.clarity.q = w.clarity.q || []).push(arguments);
      };
      const s = document.createElement("script");
      s.async = true;
      s.src = "https://www.clarity.ms/tag/yk9mdn2iu2";
      const first = document.getElementsByTagName("script")[0];
      if (first && first.parentNode) first.parentNode.insertBefore(s, first);else document.head.appendChild(s);
      /* Mask the whole booking section, so nothing typed there is recorded. */
      const mark = () => {
        const el = document.getElementById("schedule");
        if (el && !el.getAttribute("data-clarity-mask")) el.setAttribute("data-clarity-mask", "true");
      };
      mark();
      window.setTimeout(mark, 2000);
      window.setTimeout(mark, 6000);
    } catch (err) {}
  }, []);
  useEffect(() => {
    const w = window as any;
    const send = (name: string, params: any) => {
      try {
        if (typeof w.gtag === 'function') w.gtag('event', name, params);
      } catch (err) {}
    };
    const serviceOf = () => {
      const p = (window.location.pathname || '').toLowerCase();
      if (p.indexOf('kitchen') !== -1) return 'kitchen';
      if (p.indexOf('bathroom') !== -1) return 'bathroom';
      if (p.indexOf('adu') !== -1) return 'adu';
      if (p.indexOf('addition') !== -1) return 'additions';
      if (p.indexOf('roofing') !== -1) return 'roofing';
      if (p.indexOf('painting') !== -1) return 'painting';
      return 'general';
    };
    const zoneOf = (el: any) => {
      if (el.closest('[role="dialog"]')) return 'mobile_menu';
      if (el.closest('#schedule form')) return 'booking_form';
      if (el.closest('footer, #footer')) return 'footer';
      const top = el.getBoundingClientRect().top + window.scrollY;
      return top < 200 ? 'header' : 'page_body';
    };
    const base = (el: any, label: string) => ({
      service: serviceOf(),
      link_location: zoneOf(el),
      link_text: label.slice(0, 80),
      page_path: window.location.pathname
    });
    let formStarted = false;
    const onClick = (e: any) => {
      const t = e.target as any;
      const el = t && t.closest ? t.closest('a,button') : null;
      if (!el) return;
      const href = el.getAttribute ? el.getAttribute('href') || '' : '';
      const label = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (/^tel:/i.test(href)) {
        send('phone_click', base(el, label));
        return;
      }
      if (/^sms:/i.test(href)) {
        send('text_click', base(el, label));
        return;
      }
      if (/renofi|financ/i.test(href) || /^financing$/i.test(label)) {
        send('financing_click', base(el, label));
        return;
      }
      if (el.closest('#schedule')) {
        if (/free onsite project consultation/i.test(label)) {
          send('appointment_type_selected', {
            appointment_type: 'onsite',
            service: serviceOf()
          });
          return;
        }
        if (/remodeling intake call/i.test(label)) {
          send('appointment_type_selected', {
            appointment_type: 'call',
            service: serviceOf()
          });
          return;
        }
      }
      const isCtaText = /^start\s+(your\s+)?project$/i.test(label) || /request (an )?estimate|schedule your free estimate|get a quote|get started|free estimate/i.test(label);
      if (isCtaText || href.indexOf('request-estimate') !== -1 || href.indexOf('#schedule') !== -1) {
        send('cta_click', base(el, label));
      }
    };
    const onInput = (e: any) => {
      if (formStarted) return;
      const t = e.target as any;
      if (!t || !t.closest || !t.tagName) return;
      if (!/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (!t.closest('#schedule')) return;
      formStarted = true;
      send('form_start', {
        service: serviceOf(),
        page_path: window.location.pathname
      });
    };
    const marks = [25, 50, 75, 90];
    const seen: any = {};
    const mountedAt = Date.now();
    const onScroll = () => {
      /* Ignore the first moments after load. While the page is still laying
         out, scrollHeight is close to the viewport height, so a few pixels of
         scroll reads as 100% and would silently burn every milestone. */
      if (Date.now() - mountedAt < 1500) return;
      const doc = document.documentElement;
      const h = Math.max(doc.scrollHeight, document.body ? document.body.scrollHeight : 0) - window.innerHeight;
      if (h < 400) return;
      const pct = Math.round(window.scrollY / h * 100);
      for (let i = 0; i < marks.length; i++) {
        const m = marks[i];
        if (pct >= m && !seen[m]) {
          seen[m] = 1;
          send('scroll_depth', {
            percent_scrolled: m,
            service: serviceOf(),
            page_path: window.location.pathname
          });
        }
      }
    };
    document.addEventListener('click', onClick, true);
    document.addEventListener('input', onInput, true);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('input', onInput, true);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);
  useEffect(() => {
    const w = window as any;
    if ((w as any).__gncCallTracking) return;
    (w as any).__gncCallTracking = true;
    const REAL_TEXT = "(909) 906-3355";
    const REAL_DIGITS = "9099063355";
    let gfn: {
      formatted: string;
      tel: string;
    } | null = null;
    const swap = (root: ParentNode) => {
      if (!gfn) return;
      root.querySelectorAll?.('a[href^="tel:"]').forEach(a => {
        const href = a.getAttribute("href") || "";
        if (href.replace(/\D/g, "").endsWith(REAL_DIGITS)) a.setAttribute("href", "tel:" + gfn!.tel);
      });
      const walker = document.createTreeWalker(root as Node, NodeFilter.SHOW_TEXT);
      let n: Node | null;
      while (n = walker.nextNode()) {
        if (n.nodeValue && n.nodeValue.includes(REAL_TEXT)) n.nodeValue = n.nodeValue.split(REAL_TEXT).join(gfn.formatted);
      }
    };
    let observer: MutationObserver | null = null;
    const start = () => {
      if (typeof w.gtag !== "function") return false;
      w.gtag("config", "AW-18356619749/-97_CLuqu_ocEOWTj7FE", {
        phone_conversion_number: REAL_TEXT,
        phone_conversion_callback: (formatted: string, mobile: string) => {
          if (!formatted) return;
          gfn = {
            formatted,
            tel: String(mobile || formatted).replace(/[^\d+]/g, "")
          };
          swap(document.body);
          observer = new MutationObserver(muts => muts.forEach(m => m.addedNodes.forEach(node => {
            if (node.nodeType === 1) swap(node as Element);
          })));
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        }
      });
      return true;
    };
    let tries = 0;
    const timer = window.setInterval(() => {
      if (start() || ++tries > 20) window.clearInterval(timer);
    }, 500);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          try {
            const element = document.querySelector(hash);
            if (element) {
              e.preventDefault();
              element.scrollIntoView({
                behavior: "smooth",
                block: "start"
              });
            }
          } catch {
            // Silently ignore invalid selectors or unsupported characters
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="navigation-header" data-nav="light" className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md border-b border-border transition-colors">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20 gap-4">
          {/* (1) Left side: Logo */}
          <div className="flex items-center shrink-0">
            <Link to="/" className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
              <WvcLogo className="h-9 sm:h-10 md:h-12 w-auto object-contain" />
            </Link>
          </div>

          {/* (2) Center: Desktop & Tablet Navigation */}
          <div className="hidden md:flex items-center justify-center flex-1 px-2 lg:px-4">
            <MenuProvider menu_id="7">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {/* (3) Right side actions */}
          <div className="flex items-center shrink-0">
            {/* Desktop & Tablet Actions */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3">
              <Button asChild variant="outline" size="sm" className="bg-primary/10 border-transparent text-primary hover:bg-primary/20 hover:text-primary font-semibold transition-colors px-2.5 lg:px-3 text-xs lg:text-sm">
                <a href="tel:+19099063355" className="inline-flex items-center gap-1.5 lg:gap-2">
                  <Phone className="size-3.5" />
                  <span className="hidden lg:inline">(909) 906-3355</span>
                  <span className="inline lg:hidden">Call</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="sm" className="hidden lg:inline-flex bg-primary/10 border-transparent text-primary hover:bg-primary/20 hover:text-primary font-semibold transition-colors">
                <Link to="sms:9099063355" className="inline-flex items-center gap-1.5">
                  <MessageSquare className="size-3.5" />
                  <span>Text</span>
                </Link>
              </Button>
              <Button asChild variant="default" size="sm" className="bg-primary text-primary-foreground font-semibold shadow-xs hover:bg-primary/90 transition-all text-xs lg:text-sm px-3 lg:px-4">
                <Link to="/#request-estimate">
                  <span className="hidden lg:inline">Start Your Project</span>
                  <span className="inline lg:hidden">Start Project</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>

            {/* Mobile Actions & Sheet Trigger */}
            <div className="flex md:hidden items-center gap-2">
              <Button asChild variant="outline" size="icon" className="border-border text-foreground hover:bg-muted" aria-label="Call (909) 906-3355">
                <a href="tel:+19099063355">
                  <Phone className="size-4 text-primary" />
                </a>
              </Button>
              <Button asChild variant="default" size="sm" className="hidden sm:inline-flex bg-primary text-primary-foreground font-semibold shadow-xs">
                <Link to="/#request-estimate">
                  <span>Start Project</span>
                </Link>
              </Button>

              {/* Mobile Drawer Trigger */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="border-border text-foreground hover:bg-muted" aria-label="Open menu">
                    <MenuIcon className="size-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-sm bg-background border-l border-border p-6 flex flex-col justify-between">
                  <div>
                    <SheetHeader className="p-0 mb-6 text-left border-b border-border pb-4">
                      <SheetTitle className="text-left flex items-center">
                        <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                          <WvcLogo className="h-9 w-auto object-contain" />
                        </Link>
                      </SheetTitle>
                    </SheetHeader>

                    <div className="overflow-y-auto max-h-[calc(100vh-250px)] pr-1">
                      <MenuProvider menu_id="7">
                        <MobileMenuConsumer onNavigate={() => setMobileMenuOpen(false)} />
                      </MenuProvider>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-border flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-2">
                      <Button asChild variant="outline" className="w-full justify-center bg-primary/10 border-transparent text-primary hover:bg-primary/20 hover:text-primary font-semibold">
                        <a href="tel:+19099063355" className="inline-flex items-center justify-center gap-1.5">
                          <Phone className="size-4" />
                          <span>Call</span>
                        </a>
                      </Button>
                      <Button asChild variant="outline" className="w-full justify-center bg-primary/10 border-transparent text-primary hover:bg-primary/20 hover:text-primary font-semibold">
                        <Link to="sms:9099063355" className="inline-flex items-center justify-center gap-1.5">
                          <MessageSquare className="size-4" />
                          <span>Text</span>
                        </Link>
                      </Button>
                    </div>
                    <Button asChild variant="default" className="w-full justify-center bg-primary text-primary-foreground font-semibold shadow-xs">
                      <Link to="/#request-estimate" onClick={() => setMobileMenuOpen(false)}>
                        <span>Start Your Project</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}